import { getCurrentAppName } from '@doltech/core/lib/auth/authManager';
import { components } from '@doltech/dto';
import { SkillEnum, TestTypeEnum } from '@doltech/utils/lib/constants';
import { smoothScroll } from '@doltech/utils/lib/smooth-scroll';
import { get } from 'lodash';
import { getSnapshot, Instance, SnapshotIn, types as t } from 'mobx-state-tree';

import {
  UserExperience,
  UserExperienceSnapshotIn,
} from '../../question-data-model/user-experience.store';
import { UserTest } from '../../question-data-model/user-test.store';
import { DoTestListeningTestSection } from './TestSection/do-test-listening-test-section.store';
import { DoTestReadingTestSection } from './TestSection/do-test-reading-test-section.store';
import { FullTestAudio } from './full-test.store';

const IMG_PLACE_HOLDER = 'https://dummyimage.com/48x48.png';

const GAP_TIME_DURING_TEST = 2; // seconds

export const ReviewStatus = {
  NOT_STARTED: 'not_started',
  REVIEWING: 'reviewing',
  REVIEWED: 'reviewed',
} as const;

export type ReviewStatus = (typeof ReviewStatus)[keyof typeof ReviewStatus];

export const ModalType = {
  NONE: 'none',
  GO_TO_REVIEW: 'start_review_time',
  SECTION_TIME_UP: 'section_time_up',
  TEST_TIME_UP: 'test_time_up',
  SUBMIT: 'submit',
  SAVE_TEST_PROGRESS: 'save_test_progress',
  CONTINUE_OR_START_OVER: 'continue_or_start_over',
} as const;

export type ModalType = (typeof ModalType)[keyof typeof ModalType];

const TourGuide = t
  .model('TourGuide', {
    isTourGuideFinished: t.optional(t.boolean, true),
    tourIndex: t.optional(t.number, 0),
    totalSteps: t.optional(t.number, 0),
  })
  .actions((self) => ({
    finishTourGuide() {
      self.isTourGuideFinished = true;
    },
    setTotalStep(stepCount) {
      self.totalSteps = stepCount;
    },
    setTourIndex(tourIndex) {
      self.tourIndex = tourIndex;
    },
  }))
  .views((self) => ({
    get isHighlightReview() {
      return self.isTourGuideFinished === false && self.tourIndex === self.totalSteps - 2;
    },
  }));

interface InitTestSubmissionExtraProps {}

export const TestSubmissionStore = UserTest.named('TestSubmissionStore')
  .props({
    initialed: t.optional(t.boolean, false),
    inProgressTotalDurationInSeconds: t.maybeNull(t.number),
    isStartedDoingTest: t.optional(t.boolean, false),
    timeUpModalVisible: t.optional(t.boolean, false),
    sectionTimeUpModalVisible: t.optional(t.boolean, false),
    submitModalVisible: t.optional(t.boolean, false),
    submitWarnModalVisible: t.optional(t.boolean, false),
    reviewTimeUpModalVisible: t.optional(t.boolean, false),
    currentSectionIndex: t.optional(t.number, 0),
    currentAudioIndex: t.optional(t.number, 0),
    bottomNavigationExpanded: t.optional(t.boolean, true),
    listeningGuideConfirmModalVisible: t.optional(t.boolean, true),
    remainTimeInSeconds: t.maybeNull(t.number),
    remainReviewTimeInSeconds: t.maybeNull(t.number),
    testTakenTimeInSeconds: t.maybeNull(t.number),
    practiceExtendTimeInSeconds: t.maybeNull(t.number),
    userExperience: t.optional(UserExperience, {}),
    audio: t.optional(FullTestAudio, {}),
    reviewTime: t.optional(t.maybeNull(t.number), 0),
    isPreviewMode: t.optional(t.boolean, false),
    reviewedTime: t.maybeNull(t.number),
    tourGuide: t.optional(TourGuide, {
      isTourGuideFinished: true,
      tourIndex: 0,
      totalSteps: 1,
    }),
    durations: t.optional(t.array(t.number), []),

    reviewStatus: t.optional<any>(t.string, ReviewStatus.NOT_STARTED),
    modal: t.maybeNull<any>(t.string),

    bookCover: t.optional<any>(t.string, IMG_PLACE_HOLDER), // FIXME: hard code

    testSections: t.optional(
      t.maybeNull(
        t.array(
          t.union(
            {
              dispatcher: (s) => {
                switch (s.skill) {
                  case SkillEnum.READING:
                    return DoTestReadingTestSection;
                  case SkillEnum.LISTENING:
                    return DoTestListeningTestSection;
                  default:
                    throw new Error(`Cannot resolve Test Section skill "${s.skill}"`);
                }
              },
            },
            DoTestReadingTestSection
          )
        )
      ),
      []
    ),
  })
  .volatile(() => ({
    audioPlayerRef: null,
    internalPageLink: null,
    premiumStore: null,
  }))
  .views((self) => ({
    get isTourGuideFinished() {
      return self.tourGuide?.isTourGuideFinished;
    },
    get timeTakenInSeconds() {
      return self.totalDurationInSeconds - self.remainTimeInSeconds;
    },
  }))
  .views((self) => ({
    get isFullTest() {
      return TestTypeEnum.FULL_TEST === self.testType;
    },
    get defaultBackUrl() {
      const isFullTest = TestTypeEnum.FULL_TEST === self.testType;
      if (self?.internalPageLink?.staticPageLink) {
        if (isFullTest) {
          return self.internalPageLink.staticPageLink.LANDING_FULL_TEST.url;
        }
        return self.internalPageLink.staticPageLink[`LANDING_${self.skill}_PRACTICE_TEST`].url;
      }
      return null;
    },
    canNext() {
      return self.currentSectionIndex < self.totalSections - 1;
    },
    canPrev() {
      return self.currentSectionIndex > 0;
    },
    get isReviewing() {
      return self.reviewStatus === ReviewStatus.REVIEWING;
    },
    get isLastGroupInSection() {
      return (
        self.testSections[self.currentSectionIndex].currentQuestionGroupIndex ===
        self.testSections[self.currentSectionIndex].questionGroups.length - 1
      );
    },
    // LISTENING TEST only started when user starts the test & guide confirm modal is closed
    get isStartedTest() {
      if (self.modal && ModalType.NONE !== self.modal) {
        return false;
      }
      if (self.isPreviewMode) {
        return true;
      }
      if (!self.isTourGuideFinished) {
        return false;
      }
      if (self.skill === SkillEnum.LISTENING) {
        if (self.reviewStatus === ReviewStatus.REVIEWING) {
          return false;
        }
        return self.isStartedDoingTest && !self.listeningGuideConfirmModalVisible;
      }
      return self.isStartedDoingTest;
    },
  }))
  .actions((self) => ({
    preventUserAction() {
      const currentAppName = getCurrentAppName();
      return self.isPreviewMode && currentAppName === 'landing-dolacademy';
    },
    setPreviewMode(newValue) {
      self.isPreviewMode = newValue;
    },
  }))
  .actions((self) => ({
    registerPremiumStore(newPremiumStore) {
      self.premiumStore = newPremiumStore;
    },
    setDurations(durations) {
      self.durations = durations;
    },
    setInternalPageLink(newInternalPageLink) {
      self.internalPageLink = newInternalPageLink;
    },

    forcePauseAudio() {
      if (self.audioPlayerRef) {
        self.audioPlayerRef.pause();
      }
    },
    registerAudioPlayerRef(newAudioPlayerRef) {
      self.audioPlayerRef = newAudioPlayerRef;
    },
    hideModal() {
      self.modal = ModalType.NONE;
    },
    openModal(type: ModalType) {
      self.modal = type;
    },
    setListeningGuideConfirmModalVisible(visible) {
      self.listeningGuideConfirmModalVisible = visible;
    },
    init(
      data:
        | components['schemas']['UserFullTestDTO']
        | (components['schemas']['UserPracticeTestDTO'] & InitTestSubmissionExtraProps)
    ) {
      self.initialed = true;
      self.timeUpModalVisible = false;
      self.submitModalVisible = false;
      self.sectionTimeUpModalVisible = false;
      self.submitWarnModalVisible = false;
      self.bookCover = IMG_PLACE_HOLDER;

      self.createdAt = data.createdAt;
      self.id = data.id;
      self.lastModifiedAt = data.lastModifiedAt;
      self.testType = data.testType as any;

      switch (data.testType) {
        case 'FULL_TEST': {
          const testData = data as components['schemas']['UserFullTestDTO'];
          self.name = testData.test.name;
          self.testID = testData.test.id;
          self.skill = testData.test.skill as SkillEnum;
          if (testData.test.skill === SkillEnum.LISTENING) {
            if (testData.currentActiveSection < 0) {
              self.reviewStatus = ReviewStatus.REVIEWING;
              self.reviewedTime = testData.reviewedTime;
              self.listeningGuideConfirmModalVisible = false;
              self.currentSectionIndex = 0;
            } else {
              self.currentSectionIndex = testData.currentActiveSection;
              self.currentAudioIndex = testData.currentActiveSection;
            }
          }
          self.bookCover = testData.test?.coverImage?.url;
          self.totalDuration = testData.test.totalDuration * 1000;
          self.totalDurationInSeconds = testData.test.totalDuration;
          self.reviewTime = testData.test.reviewTime * 1000;
          self.totalSections = testData.testSections.length;
          self.inProgressTotalDurationInSeconds =
            testData.test.totalDuration - (testData.testTakenTimeInSeconds || 0);
          self.remainTimeInSeconds = self.inProgressTotalDurationInSeconds;
          self.remainReviewTimeInSeconds = testData.test.reviewTime;
          self.testSections.replace(testData.testSections);
          self.isPreviewMode = false;
          self.testTakenTimeInSeconds = testData.testTakenTimeInSeconds;
          break;
        }
        case 'PRACTICE_TEST': {
          const testData = data as components['schemas']['UserPracticeTestDTO'];
          self.testID = testData.testSection.id;
          self.name = testData.testSection.name;
          self.bookCover = testData.testSection?.coverImage?.url;
          self.skill = testData.testSection.skill as SkillEnum;
          self.totalDuration = testData.testSection.duration * 60 * 1000;
          self.totalSections = 1;
          self.remainTimeInSeconds = testData.testSection.duration * 60;
          self.testSections.replace([testData.testSection]);
          self.bottomNavigationExpanded = false;
          self.totalDurationInSeconds = testData.testSection.duration * 60;
          self.testTakenTimeInSeconds = testData.testTakenTimeInSeconds;

          break;
        }
        default:
      }
    },
    setStartDoingTest() {
      self.isStartedDoingTest = true;
      self.isPreviewMode = false;
    },
    setStartTourGuideline(ableToViewGuideline: boolean) {
      if (ableToViewGuideline) {
        self.tourGuide = TourGuide.create({
          isTourGuideFinished: false,
          tourIndex: 0,
          totalSteps: 0,
        });
      }
    },
    initPreviewSection(data: components['schemas']['TestSectionDTO']) {
      self.initialed = true;
      self.isPreviewMode = true;
      self.skill = data.skill as SkillEnum;
      self.testSections.replace([{ ...data }]);
    },
    initPreviewTest(data: components['schemas']['TestDetailWithTestSectionDTO']) {
      self.initialed = true;
      self.isPreviewMode = true;
      self.bookCover = IMG_PLACE_HOLDER;
      self.testID = data.id;
      self.name = data.name;
      self.skill = data.skill as SkillEnum;
      self.totalSections = data.testSections?.length;
      self.testSections.replace(data.testSections);
    },
    goToSectionIndex(sectionIndex: number, isScroll = true) {
      if (self.preventUserAction()) return;
      self.currentSectionIndex = sectionIndex;
      // self.listeningGuideConfirmModalVisible = true;
      if (isScroll) {
        const questionNo = self.testSections[sectionIndex]?.questionGroups?.[0]?.questions?.[0]?.no;
        const elmToScroll = document.getElementById(`question-no-${questionNo}`);

        if (questionNo && elmToScroll) {
          smoothScroll(400, document.getElementById('body-container'), elmToScroll.offsetTop);
        } else {
          smoothScroll(400, document.getElementById('body-container'), 0);
        }
      }
    },
    goToQuestionAtGroup(sectionIndex: number, groupIndex: number) {
      self.currentSectionIndex = sectionIndex;
      self.testSections[sectionIndex].currentQuestionGroupIndex = groupIndex;
    },
    openTimeUpModal() {
      self.timeUpModalVisible = true;
    },
    closeTimeUpModal() {
      self.timeUpModalVisible = false;
    },
    openSectionTimeUpModal() {
      self.sectionTimeUpModalVisible = true;
    },
    closeSectionTimeUpModal() {
      self.modal = undefined;
      self.sectionTimeUpModalVisible = false;
    },
    openReviewTimeUpModal() {
      self.reviewTimeUpModalVisible = true;
    },
    closeReviewTimeUpModal() {
      self.reviewTimeUpModalVisible = false;
    },
    openSubmitModal() {
      let answeredCount = 0;
      self.testSections.forEach((section) => {
        answeredCount += section.totalAnswerInGroup;
      });

      let questionCount = 0;
      self.testSections.forEach((section) => {
        questionCount += section.totalQuestionInGroup;
      });
      if (answeredCount === questionCount) {
        self.submitModalVisible = true;
        self.submitWarnModalVisible = false;
      } else {
        self.submitModalVisible = false;
        self.submitWarnModalVisible = true;
      }
    },
    closeSubmitModal() {
      self.submitModalVisible = false;
      self.submitWarnModalVisible = false;
    },
    setRemainTimeInSeconds(timeInSeconds) {
      if (self.isStartedTest) {
        self.remainTimeInSeconds = timeInSeconds;
      }
    },
    setRemainReviewTimeInSeconds() {
      self.remainReviewTimeInSeconds -= 1;
    },
    setPracticeExtendTimeInSeconds(time) {
      self.practiceExtendTimeInSeconds = time;
    },
    setUserExperience(data: UserExperienceSnapshotIn) {
      const fields = Object.keys(data);
      fields.forEach((field) => {
        self.userExperience[field] = data[field];
      });
    },
    setReviewStatus(status: ReviewStatus) {
      self.reviewStatus = status;
    },
  }))
  .actions((self) => ({
    startSection() {
      self.setListeningGuideConfirmModalVisible(false);
    },
    nextSection(isScroll = true) {
      if (self.canNext()) {
        self.goToSectionIndex(self.currentSectionIndex + 1, isScroll);
      }
    },
    prevSection(isScroll) {
      if (self.canPrev()) {
        self.goToSectionIndex(self.currentSectionIndex - 1, isScroll);
      }
    },
    nextAudio() {
      self.currentAudioIndex = self.currentAudioIndex + 1;
    },
  }))
  .views((self) => ({
    get desc() {
      if (self.testSections) {
        return (
          self.testSections.find(
            (_testSection, testSectionIndex) => testSectionIndex === self.currentSectionIndex
          )?.name || ''
        );
      }
      return '';
    },
    get isLastSection() {
      return self.currentSectionIndex === self.testSections.length - 1;
    },

    get isLastAudio() {
      return self.currentAudioIndex === self.testSections.length - 1;
    },

    get totalQuestions() {
      let count = 0;
      self.testSections.forEach((section) => {
        count += section.totalQuestionInGroup;
      });
      return count;
    },
    get answeredCount() {
      let count = 0;
      self.testSections.forEach((section) => {
        count += section.totalAnswerInGroup;
      });
      return count;
    },

    get currentTestSection() {
      return self.testSections.find((_item, index) => index === self.currentSectionIndex);
    },

    get currentAudioSection() {
      return self.testSections.find((_item, index) => index === self.currentAudioIndex);
    },

    get currentTestTakenTimeInSeconds() {
      let totalDurationTillCurrentAudio = 0;
      for (let index = 0; index < self.currentAudioIndex; index++) {
        totalDurationTillCurrentAudio =
          totalDurationTillCurrentAudio + self.durations?.[index] + GAP_TIME_DURING_TEST;
      }

      return self.timeTakenInSeconds - totalDurationTillCurrentAudio;
    },

    prepareDataForSubmit() {
      let totalAnsweredQuestions = 0;
      const userSubmitQuestions = self.testSections.map((section) => {
        const userQuestionAnswers = [];
        section.questionGroups.forEach((group) => {
          group.questions.forEach((question) => {
            const preparedQuestion = question.prepareDataForSubmit();
            totalAnsweredQuestions += question.answerCount;
            userQuestionAnswers.push(preparedQuestion);
          });
        });
        return {
          testSectionId: section.id,
          studentPassage: section.studentPassage,
          userQuestionAnswers,
        };
      });

      const totalDurationInSecond = self.isReviewing
        ? self.reviewTime / 1000
        : self.totalDuration / 1000;

      let payload:
        | components['schemas']['UserSubmitFullTestDTO']
        | components['schemas']['UserSubmitPracticeTestDTO'] = {};
      switch (self.testType) {
        case TestTypeEnum.FULL_TEST: {
          payload = {
            userExperience: getSnapshot(self.userExperience),
            userSubmitQuestions,
            testType: 'FULL_TEST',
          };

          payload.testTakenTimeInSeconds = self.isReviewing
            ? totalDurationInSecond -
              (self.remainReviewTimeInSeconds > 0 ? self.remainReviewTimeInSeconds : 0)
            : totalDurationInSecond - (self.remainTimeInSeconds > 0 ? self.remainTimeInSeconds : 0);
          break;
        }
        case TestTypeEnum.PRACTICE_TEST: {
          payload = {
            userExperience: getSnapshot(self.userExperience),
            userSubmitQuestions: userSubmitQuestions[0],
            testType: 'PRACTICE_TEST',
          };

          if (self.remainTimeInSeconds > 0) {
            payload.testTakenTimeInSeconds = totalDurationInSecond - self.remainTimeInSeconds;
          } else {
            payload.testTakenTimeInSeconds =
              totalDurationInSecond + self.practiceExtendTimeInSeconds;
          }
          break;
        }
        default:
      }

      payload.totalAnsweredQuestions = totalAnsweredQuestions;

      return payload;
    },

    getMinMaxQuestionInGroup(sectionIndex, groupIndex) {
      const testSection = self.testSections.find((_n, sIndex) => sIndex === sectionIndex);
      if (testSection) {
        const { questions } = testSection.questionGroups.find(
          (_g, gIndex) => gIndex === groupIndex
        );
        const minQuestion = questions[0].no;
        const maxQuestion =
          Number(minQuestion) +
          questions.reduce((p, n) => p + get(n, 'bottomBarQuestionNoes.length', 0), 0) -
          1;
        if (maxQuestion > minQuestion) {
          return `${minQuestion} - ${maxQuestion}`;
        }
        return `${minQuestion}`;
      }
      return '';
    },
  }))
  .actions((self) => ({
    goToFirstQuestionGroup() {
      // reset the group index of current section -- this is intend, not duplicate code
      self.currentTestSection.currentQuestionGroupIndex = 0; // https://dolenglish.atlassian.net/browse/DT-5216

      // reset the index first section
      self.currentSectionIndex = 0;
      self.currentTestSection.currentQuestionGroupIndex = 0;
    },
    goToLastQuestionGroup() {
      self.currentSectionIndex = self.testSections.length - 1;
      self.currentTestSection.currentQuestionGroupIndex =
        self.currentTestSection.questionGroups.length - 1;
    },
    nextQuestionGroup(isScroll = true) {
      if (self.preventUserAction()) return;

      if (self.currentTestSection) {
        const { currentQuestionGroupIndex, questionGroups } = self.currentTestSection;
        const isLastGroupInSection = currentQuestionGroupIndex === questionGroups.length - 1;
        if (isLastGroupInSection) {
          self.nextSection(isScroll);
          return;
        }
        self.currentTestSection.next(isScroll);
      }
    },
    prevQuestionGroup(isScroll = true) {
      if (self.preventUserAction()) return;

      if (self.currentTestSection) {
        const { currentQuestionGroupIndex } = self.currentTestSection;
        const isFirstSectionFirstGroup =
          self.currentSectionIndex === 0 && currentQuestionGroupIndex === 0;
        if (!isFirstSectionFirstGroup) {
          if (currentQuestionGroupIndex === 0) {
            const lastQuestionGroup = self.testSections.find(
              (_t, tIndex) => tIndex === self.currentSectionIndex - 1
            ).questionGroups;
            self.prevSection(isScroll);
            self.currentTestSection.currentQuestionGroupIndex = lastQuestionGroup.length - 1;
            return;
          }
          self.currentTestSection.prev(isScroll);
        }
      }
    },
    toggleExpandCollapseBottomNavigationBar() {
      self.bottomNavigationExpanded = !self.bottomNavigationExpanded;
    },
  }))
  .views((self) => ({
    get isNextPassage(): boolean {
      if (self.currentTestSection) {
        const { currentQuestionGroupIndex, questionGroups } = self.currentTestSection;
        return currentQuestionGroupIndex === questionGroups.length - 1;
      }
      return false;
    },
    get nextInfo(): string {
      if (self.currentTestSection) {
        const { currentQuestionGroupIndex, questionGroups } = self.currentTestSection;
        const isLastGroupInSection = currentQuestionGroupIndex === questionGroups.length - 1;
        if (isLastGroupInSection) {
          return self.getMinMaxQuestionInGroup(self.currentSectionIndex + 1, 0);
        }
        return self.getMinMaxQuestionInGroup(
          self.currentSectionIndex,
          currentQuestionGroupIndex + 1
        );
      }
      return '';
    },
    get prevInfo(): string {
      if (self.currentTestSection) {
        const { currentQuestionGroupIndex } = self.currentTestSection;
        const isFirstSectionFirstGroup =
          self.currentSectionIndex === 0 && currentQuestionGroupIndex === 0;
        if (!isFirstSectionFirstGroup) {
          if (currentQuestionGroupIndex === 0) {
            const lastQuestionGroup = self.testSections.find(
              (_t, tIndex) => tIndex === self.currentSectionIndex - 1
            ).questionGroups;
            return self.getMinMaxQuestionInGroup(
              self.currentSectionIndex - 1,
              lastQuestionGroup.length - 1
            );
          }
          return self.getMinMaxQuestionInGroup(
            self.currentSectionIndex,
            currentQuestionGroupIndex - 1
          );
        }
      }
      return '';
    },
  }));

export interface TestSubmissionStoreSnapshotIn extends SnapshotIn<typeof TestSubmissionStore> {}

export interface TestSubmissionStoreInstance extends Instance<typeof TestSubmissionStore> {}
